import { SimpleSelect } from "@/shared/ui/SimpleSelect";
import { Button, Input, useDebounceCallback } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { CAMP_REGISTRATION_STATUS_LABELS } from "../lib/camp-status";

type CampRegistrationsSearch = {
  limit?: number;
  offset?: number;
  sort?: Array<{ field: string; dir: "asc" | "desc" }>;
  eventId?: string;
  status?: string;
  classTypeId?: string;
  search?: string;
};

const STATUS_OPTIONS = [
  { value: "all", label: "ທຸກສະຖານະ" },
  ...Object.entries(CAMP_REGISTRATION_STATUS_LABELS).map(([value, label]) => ({
    value,
    label,
  })),
];

export function CampRegistrationsFilter({
  classOptions = [],
}: {
  classOptions?: Array<{ classTypeId: string; name: string }>;
}) {
  const nav = useNavigate({ from: "/app/camp-registrations" });
  const search = useSearch({
    from: "/app/camp-registrations",
  }) as CampRegistrationsSearch;

  const [searchValue, setSearchValue] = useState(search.search ?? "");

  useEffect(() => {
    setSearchValue(search.search ?? "");
  }, [search.search]);

  const debounced = useDebounceCallback((val: string) => {
    setSearchValue(val);
    nav({
      search: {
        ...search,
        search: val.trim() || undefined,
        offset: 0,
      },
    });
  }, 400);

  const statusValue = search.status ?? "all";
  const classTypeId = search.classTypeId ?? "all";
  const hasFilter =
    !!search.search?.trim() ||
    (statusValue !== "all" && !!statusValue) ||
    (classTypeId !== "all" && !!classTypeId);

  const clearFilters = () => {
    nav({
      search: {
        limit: search.limit,
        offset: 0,
        sort: search.sort,
        eventId: search.eventId,
      },
    });
    setSearchValue("");
  };

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <Input
            placeholder="ຄົ້ນຫາຊື່ / ເບີໂທ..."
            value={searchValue}
            onChange={(e) => debounced(e.target.value)}
            className="h-8 sm:max-w-xs"
          />
          <SimpleSelect
            value={statusValue}
            onValueChange={(val) =>
              nav({
                search: {
                  ...search,
                  status: val === "all" ? undefined : val,
                  offset: 0,
                },
              })
            }
            placeholder="ສະຖານະ"
            className="h-8 w-full sm:w-44"
            options={STATUS_OPTIONS}
          />
          {classOptions.length > 0 ? (
            <SimpleSelect
              value={classTypeId}
              onValueChange={(val) =>
                nav({
                  search: {
                    ...search,
                    classTypeId: val === "all" ? undefined : val,
                    offset: 0,
                  },
                })
              }
              placeholder="ຄລາສ"
              className="h-8 w-full sm:w-44"
              options={[
                { value: "all", label: "ທຸກຄລາສ" },
                ...classOptions.map((item) => ({
                  value: item.classTypeId,
                  label: item.name,
                })),
              ]}
            />
          ) : null}
        </div>
        {hasFilter ? (
          <Button
            variant="ghost"
            size="sm"
            className="h-8"
            onClick={clearFilters}
          >
            <XIcon className="mr-1 size-4" />
            ລ້າງຕົວກອງ
          </Button>
        ) : null}
      </div>
    </div>
  );
}
